import React, { useEffect, useRef, useState } from "react";
import { Languages, Check } from "lucide-react";
import { LocaleDefinition } from "../i18n.ts";
import { useLingui } from "@lingui/react/macro";

interface LanguageSelectorProps {
  locales: LocaleDefinition[];
  locale: string;
  handleLanguageChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  showLabel?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  locales,
  locale,
  handleLanguageChange,
  showLabel = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLingui();

  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (newLocaleId: string) => {
    const event = {
      target: { value: newLocaleId },
    } as React.ChangeEvent<HTMLSelectElement>;

    handleLanguageChange(event);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="text-soluud-text-secondary hover:bg-soluud-primary-light flex cursor-pointer items-center justify-center rounded-lg p-2 transition-colors duration-150"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t`Select Language`}
      >
        <Languages className="mr-1 h-5 w-5" />
        {showLabel &&
          (() => {
            const current = locales.find((l) => l.id === locale);
            return current ? (
              <span className="ml-1 text-sm">
                {current.flag} {current.name}
              </span>
            ) : null;
          })()}
      </button>

      {isOpen && (
        <ul
          role="listbox"
          className="border-soluud-border absolute right-0 z-10 mt-2 w-48 origin-top-right animate-[menu-enter_150ms_ease-out] overflow-hidden rounded-xl border bg-white shadow-[--shadow-elevated]"
        >
          {locales.map((loc) => {
            const isSelected = loc.id === locale;
            return (
              <li
                key={loc.id}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleOptionClick(loc.id)}
                className={`text-soluud-text-primary hover:bg-soluud-primary-light flex cursor-pointer items-center justify-between px-4 py-2.5 transition-colors duration-150 ${
                  isSelected ? "bg-soluud-primary-subtle" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  {loc.flag && <span className="text-xl">{loc.flag}</span>}
                  <span className="text-sm">{loc.name}</span>
                </div>
                {isSelected && (
                  <Check className="text-soluud-primary h-4 w-4" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
